import { useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  getAllGPanelRoles,
  getAllOrgUnitsFromGoogleQuery,
  getDomainsQuery,
  getUserGPanelRoleQuery,
  getUserQuery,
} from '../../../api/users-api';
import { Box, Stack } from '@mui/system';
import GeneralSettingsForm from './general-settings-form';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import SharedButton from '../../../shared/buttons/shared-button';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';

export default function UserGeneralSettings() {
  const { userEmail } = useParams() as { userEmail: string };

  const { data: user, isFetching } = useQuery(getUserQuery(userEmail));
  const { data: gPanelUserRole } = useQuery(getUserGPanelRoleQuery(userEmail));
  const { data: orgUnits } = useQuery(getAllOrgUnitsFromGoogleQuery());
  const { data: domains } = useQuery(getDomainsQuery());

  const { userRoles, changeLogRoles, canSeePasswords } = useUserSettings();

  const { data: gPanelRoles } = useQuery({
    queryKey: [UserKeys.gPanelRoles],
    queryFn: () => getAllGPanelRoles(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('gPanel roles'),
    },
  });

  if (
    !user ||
    !gPanelRoles ||
    !domains ||
    !orgUnits ||
    !gPanelUserRole ||
    isFetching
  ) {
    return (
      <>
        <Box sx={{ position: 'relative', height: '100%' }}>
          <Stack
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
            sx={{ position: 'absolute', top: -60, right: 0 }}
          >
            <SharedIconButton variant="refresh" tooltipLabel="Refresh" />
            <SharedButton variant="primary" disabled>
              Save
            </SharedButton>
          </Stack>

          <Box sx={{ flex: 1, height: '100%', overflowY: 'auto' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
              Basic Information
            </Typography>
            <Box sx={{ py: 2, px: 4, width: '100%' }}>
              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ pb: 2 }}
                justifyContent="space-around"
              >
                <Skeleton variant="rounded" width={400} height={53} />
                <Skeleton variant="rounded" width={400} height={53} />
              </Stack>

              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ pb: 2 }}
                justifyContent="space-around"
              >
                <Skeleton variant="rounded" width={400} height={53} />
                <Skeleton variant="rounded" width={400} height={53} />
              </Stack>

              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ pb: 2 }}
                justifyContent="space-around"
              >
                <Skeleton variant="rounded" width={400} height={53} />
                <Skeleton variant="rounded" width={400} height={53} />
              </Stack>
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
              Password
            </Typography>

            <Stack
              flexDirection="row"
              justifyContent="space-around"
              alignItems="center"
              sx={{ py: 2, px: 4, width: '100%' }}
            >
              <Stack flexDirection="row" alignItems="baseline" gap={1}>
                <Skeleton variant="rounded" width={400} height={53} />
                <Skeleton variant="circular" width={53} height={53} />
              </Stack>
              <Skeleton variant="rounded" width={400} height={53} />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
              Contact Sharing
            </Typography>

            <Stack
              flexWrap="wrap"
              sx={{
                py: 2,
                px: 4,
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'space-around',
              }}
            >
              <Skeleton variant="rounded" width={500} height={32} />
              <Box sx={{ minWidth: '400px' }} />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
              Roles
            </Typography>

            <Stack
              flexWrap="wrap"
              spacing={4}
              sx={{
                py: 2,
                px: 4,
                width: '100%',
                justifyContent: 'space-around',
                flexDirection: 'row',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Skeleton variant="rounded" width={400} height={56} />
                <Skeleton variant="rounded" width={400} height={56} />
              </Box>
              <Box sx={{ minWidth: 400 }} />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
              2-Step Verification
            </Typography>

            <Stack
              spacing={4}
              flexDirection="row"
              justifyContent="space-around"
              sx={{ py: 2, px: 4, width: '100%' }}
              flexWrap="wrap"
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Stack flexDirection="row" gap={4} sx={{ pb: 2 }}>
                  <Skeleton variant="rounded" width={150} height={40} />
                  <Skeleton variant="rounded" width={150} height={40} />
                </Stack>
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  gap={1}
                  sx={{ pb: 2 }}
                >
                  <Skeleton variant="text" width={150} />
                  <Skeleton variant="rounded" width={80} height={24} />
                </Stack>
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  gap={1}
                  sx={{ pb: 2 }}
                >
                  <Skeleton variant="text" width={150} />
                  <Skeleton variant="rounded" width={80} height={24} />
                </Stack>
                <Skeleton variant="rounded" width={160} height={40} />
              </Box>
              <Box sx={{ minWidth: 400 }} />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 300, mt: 2, mb: 1 }}>
              Recovery Information
            </Typography>

            <Stack
              flexWrap="wrap"
              sx={{
                px: 4,
                width: '100%',
                justifyContent: 'flex-start',
                flexDirection: 'row',
              }}
              spacing={2}
            >
              <Skeleton variant="text" width="100%" />
              <Stack flexDirection="row" gap={4}>
                <Skeleton variant="rounded" width={400} height={56} />
                <Skeleton variant="rounded" width={400} height={56} />
              </Stack>
            </Stack>
          </Box>
        </Box>
      </>
    );
  } else {
    return (
      <GeneralSettingsForm
        user={user}
        userRoles={userRoles}
        gPanelRoles={gPanelRoles}
        gPanelUserRole={gPanelUserRole}
        domains={domains}
        orgUnits={orgUnits}
        changeLogRoles={changeLogRoles}
        canSeePasswords={canSeePasswords}
      />
    );
  }
}
